import { useState } from "react";

interface UsageRecord {
  month: string;
  usage: number;
  cost: number;
}

interface UsageChartProps {
  usageHistory: UsageRecord[];
  userName: string;
}

export default function UsageChart({ usageHistory, userName }: UsageChartProps) {
  const [metric, setMetric] = useState<"usage" | "cost">("usage");
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const data = usageHistory;
  const values = data.map(d => metric === "usage" ? d.usage : d.cost);
  const maxVal = Math.max(...values);
  const minVal = Math.min(...values);
  const avgVal = values.reduce((a, b) => a + b, 0) / values.length;
  const totalVal = values.reduce((a, b) => a + b, 0);

  const currentMonth = data[data.length - 1];
  const prevMonth = data[data.length - 2];
  const currentVal = metric === "usage" ? currentMonth.usage : currentMonth.cost;
  const prevVal = metric === "usage" ? prevMonth.usage : prevMonth.cost;
  const changeRate = ((currentVal - prevVal) / prevVal * 100).toFixed(1);

  const formatVal = (v: number) => metric === "usage" ? `${v.toFixed(1)} m³` : `¥${v.toFixed(1)}`;

  // SVG line chart
  const chartW = 600;
  const chartH = 120;
  const padL = 10;
  const padR = 10;
  const padT = 10;
  const padB = 10;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;

  const points = data.map((d, i) => {
    const x = padL + (i / (data.length - 1)) * innerW;
    const v = metric === "usage" ? d.usage : d.cost;
    const y = padT + innerH - ((v - minVal) / (maxVal - minVal || 1)) * innerH;
    return { x, y, d };
  });

  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = `${pathD} L ${points[points.length - 1].x} ${chartH - padB} L ${points[0].x} ${chartH - padB} Z`;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">用能分析</h3>
          <p className="text-xs text-gray-400 mt-0.5">{userName} · 2025年全年数据</p>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setMetric("usage")}
            className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${metric === "usage" ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500"}`}
          >
            用气量
          </button>
          <button
            onClick={() => setMetric("cost")}
            className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${metric === "cost" ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500"}`}
          >
            费用
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[
          { label: "年度合计", value: formatVal(totalVal), color: "text-[#E8531A]" },
          { label: "月均用量", value: formatVal(avgVal), color: "text-gray-800" },
          { label: "最高月份", value: formatVal(maxVal), color: "text-red-500" },
          {
            label: "环比变化",
            value: `${Number(changeRate) >= 0 ? "+" : ""}${changeRate}%`,
            color: Number(changeRate) >= 0 ? "text-red-500" : "text-green-600",
          },
        ].map((s, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400">{s.label}</p>
            <p className={`text-sm font-bold mt-0.5 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* SVG Line Chart */}
      <div className="relative mb-2">
        <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" style={{ height: "120px" }}>
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#E8531A" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#E8531A" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          <path d={areaD} fill="url(#areaGrad)" />
          <path d={pathD} fill="none" stroke="#E8531A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={hoveredIdx === i ? 5 : 3}
              fill={hoveredIdx === i ? "#E8531A" : "white"}
              stroke="#E8531A"
              strokeWidth="2"
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            />
          ))}
          {hoveredIdx !== null && (
            <g>
              <rect
                x={Math.min(points[hoveredIdx].x - 35, chartW - 80)}
                y={points[hoveredIdx].y - 32}
                width="80"
                height="24"
                rx="4"
                fill="#1f2937"
              />
              <text
                x={Math.min(points[hoveredIdx].x - 35, chartW - 80) + 40}
                y={points[hoveredIdx].y - 16}
                textAnchor="middle"
                fill="white"
                fontSize="10"
              >
                {formatVal(metric === "usage" ? data[hoveredIdx].usage : data[hoveredIdx].cost)}
              </text>
            </g>
          )}
        </svg>
      </div>

      {/* Month labels */}
      <div className="flex justify-between px-2">
        {data.map((d, i) => (
          <span key={i} className="text-xs text-gray-400">{d.month.replace("2025-", "")+"月"}</span>
        ))}
      </div>

      {/* Bar chart below */}
      <div className="mt-5 pt-4 border-t border-gray-50">
        <p className="text-xs text-gray-400 mb-3">月度柱状对比</p>
        <div className="flex items-end gap-1.5 h-20">
          {data.map((d, i) => {
            const v = metric === "usage" ? d.usage : d.cost;
            const h = (v / maxVal) * 100;
            const isLast = i === data.length - 1;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex items-end justify-center" style={{ height: "64px" }}>
                  <div
                    className={`w-full rounded-t-sm transition-all ${isLast ? "bg-[#E8531A]" : "bg-[#E8531A]/20 hover:bg-[#E8531A]/40"}`}
                    style={{ height: `${h}%` }}
                  ></div>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap">{d.month.replace("2025-", "")}月</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
